/**
 * Calculation Engine
 * 
 * Energy-based calculation system for dog food recommendations
 * Based on Blue Zone Dog Food Calculation Engine specification
 */

class CalculationEngine {
  constructor() {
    // Fixed Constants
    this.ME_KCAL_PER_KG = 4505.03;
    this.ME_KCAL_PER_GRAM = 4.50503;
    this.UNDERFEED_FACTOR = 0.9;
    this.BAG_SIZE_GRAMS = 1000; // 1kg bags
    this.TARGET_DAYS = 25; // Target coverage for regular subscription
    this.TRANSITION_FOOD_DAYS = 3; // "Days worth" consumed during 6-day transition (25%+50%+75% mix-in)
    
    // Energy Multipliers
    this.AGE_FACTORS = {
      'puppy': 2.0,    // ≤8m
      'adult': 1.0,    // 8m to 7y
      'senior': 0.8    // ≥7y
    };
    
    this.ACTIVITY_FACTORS = {
      'indoorInactive': 1.2,
      'low': 1.4,
      'moderate': 1.6,
      'veryActive': 2.0
    };
    
    this.NEUTER_FACTORS = {
      'neutered': 1.0,
      'intact': 1.2
    };
    
    this.BODY_COMPOSITION_FACTORS = {
      'heavy': 0.85,
      'normal': 1.0,
      'slim': 1.15
    };
    
    this.TREAT_FACTORS = {
      'noTreats': 1.0,
      'fewTreats': 0.95,
      'lotOfTreats': 0.9
    };
  }

  /**
   * Calculate all recommendations for a dog
   * @param {Object} petData - Pet data object
   * @returns {Object} Calculation results
   */
  calculate(petData) {
    // Validate required inputs
    if (!this.validateInputs(petData)) {
      throw new Error('Invalid pet data for calculation');
    }

    // Step 1: Resting Energy Requirement (RER)
    const rer = this.calculateRER(petData.weight);

    // Step 2: Get energy multipliers
    const multipliers = this.getEnergyMultipliers(petData);

    // Step 3: Maintenance Energy Requirement (MER)
    const mer = this.calculateMER(rer, multipliers);

    // Step 4: Recommended Daily Calories
    const dailyKcal = this.calculateDailyKcal(mer, multipliers.treatFactor);

    // Step 5: Recommended Daily Food Amount (grams)
    const dailyFoodG = this.calculateDailyFoodG(dailyKcal);

    // Step 6: Days Per Bag
    const daysPerBag = this.calculateDaysPerBag(dailyFoodG);

    // Step 7: Bag Count Logic
    const bagCount = this.calculateBagCount(petData.weight, daysPerBag);
    const introBagCount = this.calculateIntroBagCount(daysPerBag);

    // Step 8: Match Days
    const matchDays = this.calculateMatchDays(daysPerBag, bagCount);
    const introMatchDays = this.calculateIntroMatchDays(daysPerBag, introBagCount);

    return {
      // Core outputs
      dailyKcal: Math.round(dailyKcal * 100) / 100, // Round to 2 decimals
      dailyFoodG: Math.round(dailyFoodG * 100) / 100,
      daysPerBag: Math.round(daysPerBag * 100) / 100,
      
      // Bag counts
      bagCount: bagCount,
      introBagCount: introBagCount,
      
      // Match days
      matchDays: matchDays,
      introMatchDays: introMatchDays,
      transitionFoodDays: this.TRANSITION_FOOD_DAYS,

      // Intermediate values (for debugging/display)
      rer: Math.round(rer * 100) / 100,
      mer: Math.round(mer * 100) / 100,
      multipliers: multipliers
    };
  }

  /**
   * Validate required inputs
   * @param {Object} petData - Pet data object
   * @returns {boolean} True if valid
   */
  validateInputs(petData) {
    return (
      petData.weight > 0 &&
      petData.ageGroup &&
      petData.activity &&
      petData.spayed !== null &&
      petData.composition &&
      petData.snacks
    );
  }

  /**
   * Step 1: Calculate Resting Energy Requirement (RER)
   * @param {number} dogWeightKg - Dog's weight in kilograms
   * @returns {number} RER in kcal per day
   */
  calculateRER(dogWeightKg) {
    return 70 * Math.pow(dogWeightKg, 0.75);
  }

  /**
   * Step 2: Get energy multipliers
   * @param {Object} petData - Pet data object
   * @returns {Object} Multiplier values
   */
  getEnergyMultipliers(petData) {
    // Age factor
    const ageFactor = this.AGE_FACTORS[petData.ageGroup] || 1.0;

    // Activity factor
    // If working dog checkbox is checked, override to veryActive
    let activityLevel = petData.activity;
    if (petData.isWorkingDog) {
      activityLevel = 'veryActive';
    }
    const activityFactor = this.ACTIVITY_FACTORS[activityLevel] || 1.0;

    // Neuter factor
    const neuterStatus = petData.spayed ? 'neutered' : 'intact';
    const neuterFactor = this.NEUTER_FACTORS[neuterStatus] || 1.0;

    // Body composition factor
    const bodyCompositionFactor = this.BODY_COMPOSITION_FACTORS[petData.composition] || 1.0;

    // Treat factor
    const treatFactor = this.TREAT_FACTORS[petData.snacks] || 1.0;

    return {
      ageFactor,
      activityFactor,
      neuterFactor,
      bodyCompositionFactor,
      treatFactor
    };
  }

  /**
   * Step 3: Calculate Maintenance Energy Requirement (MER)
   * @param {number} rer - Resting Energy Requirement
   * @param {Object} multipliers - Energy multipliers
   * @returns {number} MER in kcal per day
   */
  calculateMER(rer, multipliers) {
    return rer * 
           multipliers.ageFactor * 
           multipliers.activityFactor * 
           multipliers.neuterFactor * 
           multipliers.bodyCompositionFactor;
  }

  /**
   * Step 4: Calculate Recommended Daily Calories
   * @param {number} mer - Maintenance Energy Requirement
   * @param {number} treatFactor - Treat factor
   * @returns {number} Daily calories
   */
  calculateDailyKcal(mer, treatFactor) {
    return mer * treatFactor * this.UNDERFEED_FACTOR;
  }

  /**
   * Step 5: Calculate Recommended Daily Food Amount (grams)
   * @param {number} dailyKcal - Daily calories
   * @returns {number} Daily food in grams
   */
  calculateDailyFoodG(dailyKcal) {
    return dailyKcal / this.ME_KCAL_PER_GRAM;
  }

  /**
   * Step 6: Calculate Days Per Bag
   * @param {number} dailyFoodG - Daily food in grams
   * @returns {number} Days per bag
   */
  calculateDaysPerBag(dailyFoodG) {
    return this.BAG_SIZE_GRAMS / dailyFoodG;
  }

  /**
   * Step 7a: Calculate Bag Count
   * @param {number} dogWeightKg - Dog's weight in kilograms
   * @param {number} daysPerBag - Days per bag
   * @returns {number} Number of bags
   */
  calculateBagCount(dogWeightKg, daysPerBag) {
    // Very small dogs (< 3.480kg) with short coverage (< 28 days) receive 2 bags
    if (dogWeightKg < 3.480 && daysPerBag < 28) {
      return 2;
    }
    
    // Small dogs (< 3kg) normally receive 1 bag
    if (dogWeightKg < 3) {
      return 1;
    }
    
    // All other dogs receive enough bags to cover at least 25 days
    return Math.max(1, Math.ceil(this.TARGET_DAYS / daysPerBag));
  }

  /**
   * Step 7b: Calculate Intro Bag Count
   * Transition model: 6 calendar days consume 3 "days worth" of food.
   * introMatchDays >= 7 requires introBagCount × daysPerBag >= 4.
   * @param {number} daysPerBag - Days per bag
   * @returns {number} Number of bags for intro box
   */
  calculateIntroBagCount(daysPerBag) {
    return Math.max(1, Math.ceil(4 / daysPerBag));
  }

  /**
   * Step 8a: Calculate Match Days (Subscription)
   * @param {number} daysPerBag - Days per bag
   * @param {number} bagCount - Number of bags
   * @returns {number} Match days
   */
  calculateMatchDays(daysPerBag, bagCount) {
    return Math.round(daysPerBag * bagCount);
  }

  /**
   * Step 8b: Calculate Intro Match Days
   * Transition model: 6 calendar days consume 3 "days worth". Remaining food covers days at 100%.
   * introMatchDays = 6 + (introBagCount × daysPerBag - 3) = 3 + introBagCount × daysPerBag.
   * @param {number} daysPerBag - Days per bag
   * @param {number} introBagCount - Number of intro bags
   * @returns {number} Intro match days
   */
  calculateIntroMatchDays(daysPerBag, introBagCount) {
    const raw = Math.max(7, this.TRANSITION_FOOD_DAYS + introBagCount * daysPerBag);
    return Math.round(raw);
  }

  /**
   * Calculate flavor distribution (see flavor-distribution.js for priority order).
   * @param {number} bagCount - Total number of bags
   * @param {Array<string>} selectedFlavors - Selected flavor IDs (e.g., ['rund', 'kip', 'vis'])
   * @returns {Object} Distribution of bags per flavor
   */
  calculateFlavorDistribution(bagCount, selectedFlavors) {
    if (typeof FlavorDistribution === 'undefined') {
      throw new Error('flavor-distribution.js must be loaded before wizard-1-calculations.js');
    }
    return FlavorDistribution.calculateFlavorDistribution(bagCount, selectedFlavors);
  }
}

// Export for global access
window.CalculationEngine = CalculationEngine;

